import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AuthImg } from "@/lib/authMedia";
import { chooseFaceLockPicture, drawFaceLockPictureChoices, type FaceLockAvatar } from "@/lib/facelock";

// "Choose its picture" (owner rule, 2026-09-30, final: people judge likeness,
// a score never does). Every picture the avatar's draw kept, large; its owner
// (or an admin) picks the one its card shows. A picture the server flagged as
// broken (no face, or clearly somebody else) says so in plain words and can
// still be picked. No score is ever shown. Free, allowed on a locked avatar,
// never changes the version. A picture drawn before its choices were kept
// shows alone, with "Draw new pictures to choose from".

type Props = {
  avatar: FaceLockAvatar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Something changed on the server (a pick saved, new pictures started): read the list again. */
  onChanged: () => void;
};

export default function ChoosePictureDialog({ avatar, open, onOpenChange, onChanged }: Props) {
  const { t } = useTranslation();
  const choices = React.useMemo(() => avatar?.picture_choices ?? [], [avatar]);
  const shown = choices.find((c) => c.chosen)?.index ?? null;
  const [selected, setSelected] = React.useState<number | null>(shown);
  const [busy, setBusy] = React.useState<"saving" | "drawing" | null>(null);
  const [error, setError] = React.useState("");
  const [started, setStarted] = React.useState(false);
  const avatarId = avatar?.id || "";

  // Another avatar, another set of pictures, or the dialog opened again.
  React.useEffect(() => {
    setSelected(shown);
    setError("");
    setStarted(false);
  }, [avatarId, shown, open]);

  if (!avatar || !avatar.picture_url) return null;

  const single = choices.length < 2;

  const save = async () => {
    if (selected === null || busy) return;
    if (selected === shown) { onOpenChange(false); return; }
    setBusy("saving");
    setError("");
    const res = await chooseFaceLockPicture(avatar.id, selected);
    setBusy(null);
    if (res.ok === false) {
      if (res.error === "picture_unknown") { setError(t("faceLock.errors.picture_unknown")); onChanged(); return; }
      setError(t("faceLock.choosePicture.failed"));
      return;
    }
    onChanged();
    onOpenChange(false);
  };

  const drawNew = async () => {
    if (busy) return;
    setBusy("drawing");
    setError("");
    const res = await drawFaceLockPictureChoices(avatar.id);
    setBusy(null);
    if (res.ok === false) { setError(t("faceLock.choosePicture.drawFailed")); return; }
    setStarted(true);
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent overlayClassName="z-[400]" className="z-[400] max-h-[92vh] overflow-y-auto sm:max-w-4xl" data-testid="facelock-choose-picture-dialog">
        <DialogHeader>
          <DialogTitle>{t("faceLock.choosePicture.title", { name: avatar.name })}</DialogTitle>
          <DialogDescription>{t("faceLock.choosePicture.lead")}</DialogDescription>
        </DialogHeader>

        {single ? (
          <div className="grid gap-3">
            <div className="mx-auto w-full max-w-[260px] overflow-hidden rounded-lg border border-border bg-muted">
              <AuthImg
                src={avatar.picture_url}
                alt={t("faceLock.pictureAlt", { name: avatar.name })}
                data-testid="facelock-choose-only"
                style={{ width: "100%", aspectRatio: "720 / 1248", objectFit: "cover", display: "block" }}
              />
            </div>
            <p className="text-sm text-muted-foreground" data-testid="facelock-choose-only-note">{t("faceLock.choosePicture.onlyOne")}</p>
            {started ? (
              <p role="status" className="text-sm" data-testid="facelock-choose-started">{t("faceLock.choosePicture.drawStarted")}</p>
            ) : avatar.can_draw_choices ? (
              <Button type="button" variant="outline" data-testid="facelock-choose-draw" disabled={!!busy} onClick={() => void drawNew()} className="w-fit">
                <RefreshCw aria-hidden /> {busy === "drawing" ? t("faceLock.choosePicture.drawing") : t("faceLock.choosePicture.drawNew")}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="radiogroup" aria-label={t("faceLock.choosePicture.title", { name: avatar.name })}>
            {choices.map((c, n) => {
              const on = selected === c.index;
              return (
                <button
                  key={c.index}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  data-testid={`facelock-choice-${c.index}`}
                  data-broken={c.broken ? "true" : "false"}
                  disabled={!!busy}
                  onClick={() => { setSelected(c.index); setError(""); }}
                  className={`grid content-start gap-1.5 rounded-lg border p-1.5 text-left transition ${on ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/60"}`}
                >
                  <div className="relative overflow-hidden rounded-md bg-muted">
                    <AuthImg
                      src={c.url}
                      alt={t("faceLock.choosePicture.pick", { n: n + 1 })}
                      loading="lazy"
                      style={{ width: "100%", aspectRatio: "720 / 1248", objectFit: "cover", display: "block" }}
                    />
                    {on && (
                      <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-4 w-4" aria-hidden />
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium">
                    {t("faceLock.choosePicture.pick", { n: n + 1 })}
                    {c.chosen && <span className="text-muted-foreground"> · {t("faceLock.choosePicture.showing")}</span>}
                  </span>
                  {c.broken && (
                    <span className="flex items-start gap-1 text-xs leading-snug text-amber-500" data-testid={`facelock-choice-broken-${c.index}`}>
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden /> {t("faceLock.choosePicture.broken")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {error && <div role="alert" data-testid="facelock-choose-error" className="text-sm text-red-500">{error}</div>}

        <div className="flex flex-wrap gap-2">
          {!single && (
            <Button type="button" data-testid="facelock-choose-save" disabled={selected === null || !!busy} onClick={() => void save()}>
              {busy === "saving" ? t("faceLock.choosePicture.saving") : t("faceLock.choosePicture.use")}
            </Button>
          )}
          <Button type="button" variant="ghost" disabled={!!busy} onClick={() => onOpenChange(false)}>
            {t("faceLock.choosePicture.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
